import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { endOfMonth, endOfWeek, format, startOfMonth, startOfWeek } from "date-fns";
import { PromiseStatuses } from "../../../lib/utilis/index.ts";
import { FinancialAdvisorAvailabilityUpdateDTO } from "../dto.ts";
import { AppointmentFiltersDTO, AppointmentResponseDTO, AppointmentsCreateDTO, AppointmentsDTO, AppointmentsStatus, AppointmentsTypology, AppointmentsUpdateRequestDTO, UpdateStatusRequest } from "./dto.ts";
import { NewAppointmentsService } from "./service.ts";


interface AppointmentsState {
    availabilityFinancialAdvisorStatus: PromiseStatuses
    availabilityFinancialAdvisorRequest: FinancialAdvisorAvailabilityUpdateDTO
    settingTab: number
    appointmentsCreationRequest: AppointmentsCreateDTO
    appointmentsCreationStatus: PromiseStatuses
    newAppointmentModal: boolean
    filters: {
        week: AppointmentFiltersDTO,
        month: AppointmentFiltersDTO,
        company: AppointmentFiltersDTO,
        financialAdvisor: AppointmentFiltersDTO
        companyAll: AppointmentFiltersDTO
    }
    appointments: {
        week: {
            status: PromiseStatuses
            data?: AppointmentResponseDTO
        },
        month: {
            status: PromiseStatuses
            data?: AppointmentResponseDTO
        },
        company: {
            status: PromiseStatuses,
            data?: AppointmentResponseDTO
        },
        financialAdvisor: {
            status: PromiseStatuses,
            data?: AppointmentResponseDTO
        },
        companyAll: {
            status: PromiseStatuses,
            data?: AppointmentResponseDTO
        },
    }
    period: 'month' | 'week' | 'company' | 'financialAdvisor'
    appointmentsDeleteStatus: PromiseStatuses
    openAppointmentConfirmOrDenyModal: boolean
    appointmentsRescheduleRequest: AppointmentsUpdateRequestDTO
    appointmentsRescheduleStatus: PromiseStatuses
    modalAppointmentType: 'save' | 'edit'
    findByIdAppointmentStatus: PromiseStatuses
    findByIdAppointmentResponse?: AppointmentsDTO
    updateStatusAppointmentStatus: PromiseStatuses
    updateStatusAppointmentRequest: UpdateStatusRequest
}

const initialState: AppointmentsState = {
    availabilityFinancialAdvisorRequest: {
        availabilities: [],
        duration: 15
    },
    availabilityFinancialAdvisorStatus: "idle",
    settingTab: 0,
    appointmentsCreationRequest: {
        startDate: "",
        endDate: "",
        title: "Appuntamento",
        notes: "",
        financialAdvisorId: "",
        companyId: "",
        typology: AppointmentsTypology.consulting,
        status: AppointmentsStatus.idle
    },
    appointmentsCreationStatus: "idle",
    newAppointmentModal: false,
    filters: {
        week: {
            page: 0,
            sort: undefined,
            fromDate: format(startOfWeek(new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd"),
            toDate: format(endOfWeek(new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd"),
            financialAdvisorId: undefined,
            companyId: undefined,
            itemsPerPage: 0,
            order: true
        },
        month: {
            page: 0,
            sort: "startDate",
            fromDate: format(startOfMonth(new Date()), "yyyy-MM-dd"),
            toDate: format(endOfMonth(new Date()), "yyyy-MM-dd"),
            financialAdvisorId: undefined,
            companyId: undefined,
            itemsPerPage: 0,
            order: true
        },
        company: {
            page: 0,
            sort: undefined,
            fromDate: undefined,
            toDate: undefined,
            financialAdvisorId: undefined,
            companyId: undefined,
            itemsPerPage: 0,
            order: true
        },
        financialAdvisor: {
            page: 0,
            sort: undefined,
            fromDate: undefined,
            toDate: undefined,
            financialAdvisorId: undefined,
            companyId: undefined,
            itemsPerPage: 0,
            order: true
        },
        companyAll: {
            page: 0,
            sort: undefined,
            fromDate: undefined,
            toDate: undefined,
            financialAdvisorId: undefined,
            companyId: undefined,
            itemsPerPage: 0,
            order: true
        },
    },
    appointments: {
        week: {
            status: 'idle'
        },
        month: {
            status: 'idle'
        },
        company: {
            status: "idle"
        },
        financialAdvisor: {
            status: "idle"
        },
        companyAll: {
            status: "idle"
        },
    },
    period: 'month',
    appointmentsDeleteStatus: "idle",
    openAppointmentConfirmOrDenyModal: false,
    appointmentsRescheduleRequest: {
        startDate: "",
        endDate: "",
    },
    appointmentsRescheduleStatus: "idle",
    modalAppointmentType: "save",
    findByIdAppointmentStatus: "idle",
    updateStatusAppointmentStatus: "idle",
    updateStatusAppointmentRequest: {
        status: AppointmentsStatus.idle
    }
}

export const updateAvailabilitesFinancialAdvisor = createAsyncThunk(
    'appointments/updateAvailabilitesFinancialAdvisor',
    async (request: { id: string, request: FinancialAdvisorAvailabilityUpdateDTO }, thunkApi): Promise<void> => {
        const appointmentsService = NewAppointmentsService()

        return appointmentsService.updateAvailabilitesFinancialAdvisor(request.id, request.request)
    }
)

export const appointmentsCreate = createAsyncThunk(
    'appontiments/appointmentsCreate',
    async (request: AppointmentsCreateDTO): Promise<void> => {
        const appointmentsService = NewAppointmentsService()

        return appointmentsService.appointmentsCreate(request)
    }
)

export const findAllAppointments = createAsyncThunk(
    'appontiments/findAllAppointments',
    async (request: { filters: AppointmentFiltersDTO, period: 'month' | 'week' | 'company' | 'financialAdvisor' | 'companyAll' }, thunkApi): Promise<{ response: AppointmentResponseDTO, period: 'month' | 'week' | 'company' | 'financialAdvisor' | 'companyAll' }> => {
        const appointmentsService = NewAppointmentsService()

        const response = await appointmentsService.findAllAppointments(request.filters)

        return {
            response,
            period: request.period
        }
    }
)

export const appointmentsDelete = createAsyncThunk(
    'appontiments/appointmentsDelete',
    async (id: string): Promise<void> => {
        const appointmentsService = NewAppointmentsService()

        return appointmentsService.appointmentsDelete(id)
    }
)

export const findByIdAppointment = createAsyncThunk(
    'appontiments/findByIdAppointment',
    async (id: string): Promise<AppointmentsDTO> => {
        const appointmentsService = NewAppointmentsService()

        return appointmentsService.findByIdAppointment(id)
    }
)

export const rescheduleAppointment = createAsyncThunk(
    'appontiments/rescheduleAppointment',
    async (request: { id: string, request: AppointmentsUpdateRequestDTO }, thunkApi): Promise<void> => {
        const appointmentsService = NewAppointmentsService()

        return appointmentsService.rescheduleAppointment(request.id, request.request)
    }
)

export const updateStatusAppointment = createAsyncThunk(
    'appontiments/updateStatusAppointment',
    async (request: { id: string, request: UpdateStatusRequest }, thunkApi): Promise<void> => {
        const appointmentsService = NewAppointmentsService()

        return appointmentsService.updateStatusAppointment(request.id, request.request)
    }
)

const appointmentsSlice = createSlice({
    name: 'financialAdvisor/slice',
    initialState,
    extraReducers(builder) {
        builder
            .addCase(updateAvailabilitesFinancialAdvisor.pending, (state) => {
                state.availabilityFinancialAdvisorStatus = 'loading'
            })
            .addCase(updateAvailabilitesFinancialAdvisor.fulfilled, (state, action) => {
                state.availabilityFinancialAdvisorStatus = 'successfully'
            })
            .addCase(updateAvailabilitesFinancialAdvisor.rejected, (state) => {
                state.availabilityFinancialAdvisorStatus = 'failed'
            })
            .addCase(appointmentsCreate.pending, (state) => {
                state.appointmentsCreationStatus = 'loading'
            })
            .addCase(appointmentsCreate.fulfilled, (state, action) => {
                state.appointmentsCreationStatus = 'successfully'
            })
            .addCase(appointmentsCreate.rejected, (state) => {
                state.appointmentsCreationStatus = 'failed'
            })
            .addCase(findAllAppointments.pending, (state, action) => {
                const { period } = action.meta.arg;

                state.appointments[period] = {
                    ...state.appointments[period],
                    data: {
                        data: [],
                        paginationData: {
                            total: 0,
                            page: 0,
                            perPage: 0,
                            prev: 0,
                            next: 0,
                            totalPage: 0
                        }
                    },
                    status: 'loading',
                };
            })
            .addCase(findAllAppointments.fulfilled, (state, action) => {
                const { period, response } = action.payload;
                state.appointments[period] = {
                    ...state.appointments[period],
                    data: response,
                    status: 'successfully',
                };
            })
            .addCase(findAllAppointments.rejected, (state, action) => {
                const { period } = action.meta.arg;

                state.appointments[period] = {
                    ...state.appointments[period],
                    data: {
                        data: [],
                        paginationData: {
                            total: 0,
                            page: 0,
                            perPage: 0,
                            prev: 0,
                            next: 0,
                            totalPage: 0
                        }
                    },
                    status: 'failed',
                };
            })
            .addCase(appointmentsDelete.pending, (state) => {
                state.appointmentsDeleteStatus = 'loading'
            })
            .addCase(appointmentsDelete.fulfilled, (state, action) => {
                state.appointmentsDeleteStatus = 'successfully'
            })
            .addCase(appointmentsDelete.rejected, (state) => {
                state.appointmentsDeleteStatus = 'failed'
            })
            .addCase(findByIdAppointment.pending, (state) => {
                state.findByIdAppointmentStatus = 'loading'
            })
            .addCase(findByIdAppointment.fulfilled, (state, action) => {
                state.findByIdAppointmentStatus = 'successfully'
                state.findByIdAppointmentResponse = action.payload
            })
            .addCase(findByIdAppointment.rejected, (state) => {
                state.findByIdAppointmentStatus = 'failed'
            })
            .addCase(rescheduleAppointment.pending, (state) => {
                state.appointmentsRescheduleStatus = 'loading'
            })
            .addCase(rescheduleAppointment.fulfilled, (state, action) => {
                state.appointmentsRescheduleStatus = 'successfully'
            })
            .addCase(rescheduleAppointment.rejected, (state) => {
                state.appointmentsRescheduleStatus = 'failed'
            })
            .addCase(updateStatusAppointment.pending, (state) => {
                state.updateStatusAppointmentStatus = 'loading'
            })
            .addCase(updateStatusAppointment.fulfilled, (state, action) => {
                state.updateStatusAppointmentStatus = 'successfully'
            })
            .addCase(updateStatusAppointment.rejected, (state) => {
                state.updateStatusAppointmentStatus = 'failed'
            })
    },
    reducers: {
        setAvailabilities: (state, action) => {
            state.availabilityFinancialAdvisorRequest.availabilities = action.payload
        },
        setSettingTab: (state, action) => {
            state.settingTab = action.payload
        },
        setSelectedDuration: (state, action) => {
            state.availabilityFinancialAdvisorRequest.duration = action.payload
        },
        setUpdateAvailabilitesFinancialAdvisorStatus: (state, action) => {
            state.availabilityFinancialAdvisorStatus = action.payload
        },
        setNewAppointmentModal: (state, action) => {
            state.newAppointmentModal = action.payload
        },
        setAppointmentCreateRequestTitle: (state, action) => {
            state.appointmentsCreationRequest.title = action.payload
        },
        setAppointmentCreateRequestTypology: (state, action) => {
            state.appointmentsCreationRequest.typology = action.payload
        },
        setAppointmentCreateRequestNotes: (state, action) => {
            state.appointmentsCreationRequest.notes = action.payload
        },
        setAppointmentCreateRequestStartDate: (state, action) => {
            state.appointmentsCreationRequest.startDate = action.payload
        },
        setAppointmentCreateRequestEndDate: (state, action) => {
            state.appointmentsCreationRequest.endDate = action.payload
        },
        setAppointmentCreateRequestCompanyId: (state, action) => {
            state.appointmentsCreationRequest.companyId = action.payload
        },
        setFilters: (
            state,
            action: PayloadAction<{ period: 'month' | 'week' | 'company' | 'financialAdvisor' | 'companyAll'; filters: Partial<AppointmentFiltersDTO> }>
        ) => {
            const { period, filters, } = action.payload;
            state.filters[period] = {
                ...state.filters[period],
                ...filters
            };
        },
        setPeriod: (state, action) => {
            state.period = action.payload;
        },
        setFindEventsStatus: (
            state,
            action: PayloadAction<{ period: 'month' | 'week' | 'company' | 'financialAdvisor' | 'companyAll', status: PromiseStatuses }>
        ) => {
            const { period, status } = action.payload;
            state.appointments[period] = {
                ...state.appointments[period],
                status,
                data: state.appointments[period].data
            }
        },
        resetNewAppointmentRequest: (state) => {
            state.appointmentsCreationRequest.title = "Appuntamento"
            state.appointmentsCreationRequest.notes = ""
            state.appointmentsCreationRequest.startDate = ""
            state.appointmentsCreationRequest.endDate = ""
            state.appointmentsCreationRequest.typology = AppointmentsTypology.consulting
        },
        setAppointmentsDeleteStatus: (state, action) => {
            state.appointmentsDeleteStatus = action.payload
        },
        setOpenAppointmentConfirmOrDenyModal: (state, action) => {
            state.openAppointmentConfirmOrDenyModal = action.payload
        },
        setAppointmentRescheduleRequestStartDate: (state, action) => {
            state.appointmentsRescheduleRequest.startDate = action.payload
        },
        setAppointmentRescheduleRequestEndDate: (state, action) => {
            state.appointmentsRescheduleRequest.endDate = action.payload
        },
        setModalAppointmentType: (state, action) => {
            state.modalAppointmentType = action.payload
        },
        setFindByIdAppointmentStatus: (state, action) => {
            state.findByIdAppointmentStatus = action.payload
        },
        setRescheduleAppointmentStatus: (state, action) => {
            state.appointmentsRescheduleStatus = action.payload
        },
        setAppointmentCreareStatus: (state, action) => {
            state.appointmentsCreationStatus = action.payload
        },
        setAppointmentDeleteStatus: (state, action) => {
            state.appointmentsDeleteStatus = action.payload
        },
        setUpdateStatusAppointmentStatus: (state, action) => {
            state.updateStatusAppointmentStatus = action.payload
        },
        setCompanyFromDate: (state, action) => {
            state.filters.company.fromDate = action.payload
        },
        setCompanyToDate: (state, action) => {
            state.filters.company.toDate = action.payload
        },
        setFindByIdAppointmentResponse: (state, action) => {
            state.findByIdAppointmentResponse = action.payload
        }
    },
})

export const {
    setAvailabilities,
    setSettingTab,
    setSelectedDuration,
    setUpdateAvailabilitesFinancialAdvisorStatus,
    setNewAppointmentModal,
    setAppointmentCreateRequestEndDate,
    setAppointmentCreateRequestNotes,
    setAppointmentCreateRequestStartDate,
    setAppointmentCreateRequestTitle,
    setAppointmentCreateRequestTypology,
    setAppointmentCreateRequestCompanyId,
    resetNewAppointmentRequest,
    setFilters,
    setFindEventsStatus,
    setPeriod,
    setAppointmentsDeleteStatus,
    setOpenAppointmentConfirmOrDenyModal,
    setModalAppointmentType,
    setFindByIdAppointmentStatus,
    setAppointmentRescheduleRequestEndDate,
    setAppointmentRescheduleRequestStartDate,
    setRescheduleAppointmentStatus,
    setAppointmentCreareStatus,
    setAppointmentDeleteStatus,
    setUpdateStatusAppointmentStatus,
    setCompanyFromDate,
    setCompanyToDate,
    setFindByIdAppointmentResponse
} = appointmentsSlice.actions

export default appointmentsSlice.reducer

